/**
 * Safe JSON-LD primitive. Renders structured data into a <script> tag
 * without using dangerouslySetInnerHTML.
 *
 * Children-as-text is the safe pattern in React for <script> tags:
 * the content is parsed as raw HTML text. The only XSS surface is a
 * literal `</script>` substring inside the JSON payload, plus U+2028
 * and U+2029 line separators that some JSON parsers handle differently
 * from JS parsers. We escape all of them defensively.
 *
 * The react/no-danger lint rule is enabled repo-wide. This component
 * is the only sanctioned way to inject JSON-LD. Future structured-data
 * additions MUST use it.
 *
 * Usage:
 *   <JsonLd data={schema} />
 *   <JsonLd data={[articleSchema, faqSchema]} id="page-jsonld" />
 */

type JsonLdData = Record<string, unknown> | Record<string, unknown>[]

const LINE_SEP_PATTERN = new RegExp(' ', 'g')
const PARA_SEP_PATTERN = new RegExp(' ', 'g')

function escapeForScript(json: string): string {
  return json
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(LINE_SEP_PATTERN, '\\u2028')
    .replace(PARA_SEP_PATTERN, '\\u2029')
}

export default function JsonLd({
  data,
  id,
}: {
  data: JsonLdData
  id?: string
}) {
  const json = escapeForScript(JSON.stringify(data))
  return (
    <script type='application/ld+json' id={id}>
      {json}
    </script>
  )
}
